'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Textarea } from '@/app/components/ui/textarea';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { Link } from '@/i18n/routing';
import type { ArtistProgramApplicationRequest } from '../_apis';
import { submitApplication } from '../_apis';

type DomesticChannel = 'xiaohongshu' | 'bilibili' | 'video_account' | 'douyin';
type GlobalChannel = 'youtube' | 'tiktok' | 'x' | 'instagram' | 'other';
type Channel = DomesticChannel | GlobalChannel;

const domesticChannels: DomesticChannel[] = ['xiaohongshu', 'bilibili', 'video_account', 'douyin'];
const globalChannels: GlobalChannel[] = ['youtube', 'tiktok', 'x', 'instagram', 'other'];
const globalCountries = [
  'Brazil',
  'Canada',
  'France',
  'Germany',
  'India',
  'Indonesia',
  'Japan',
  'Malaysia',
  'Mexico',
  'Nigeria',
  'Pakistan',
  'Philippines',
  'Saudi Arabia',
  'South Korea',
  'Spain',
  'Turkey',
  'United Kingdom',
  'United States',
  'Vietnam',
] as const;
const fields = [
  'finance',
  'technology',
  'digital',
  'history',
  'traditional_culture',
  'cute',
  'emotion',
  'health',
  'film',
  'animation',
  'games',
  'sports',
  'fitness',
  'city_culture_museum',
  'travel',
  'entertainment',
  'dance',
  'food',
  'art',
  'home',
  'real_estate',
  'social_news',
  'military',
  'other',
] as const;
const scenes = [
  'self_media',
  'advertising_marketing',
  'art_creation',
  'professional_film',
  'school_work',
  'workplace',
  'education_cultural_tourism',
  'other',
] as const;
type FormState = {
  roles: string[];
  country: string;
  countryOther: string;
  personalIntro: string;
  school: string;
  major: string;
  creationFields: string[];
  creationFieldOther: string;
  usageScenes: string[];
  usageSceneOther: string;
  workplaceDescription: string;
  socials: Record<Channel, string>;
  followerRange: string;
  featuredWorkUrl: string;
  contact: string;
  agreed: boolean;
};
const emptyForm = (): FormState => ({
  roles: [],
  country: '',
  countryOther: '',
  personalIntro: '',
  school: '',
  major: '',
  creationFields: [],
  creationFieldOther: '',
  usageScenes: [],
  usageSceneOther: '',
  workplaceDescription: '',
  socials: {
    xiaohongshu: '',
    bilibili: '',
    video_account: '',
    douyin: '',
    youtube: '',
    tiktok: '',
    x: '',
    instagram: '',
    other: '',
  },
  followerRange: '',
  featuredWorkUrl: '',
  contact: '',
  agreed: false,
});
const MAX = { personalIntro: 500, school: 50, major: 50, other: 150 } as const;

export function ApplicationDialog({
  open,
  onClose,
  onSubmitted,
  view = 'application',
  status,
}: {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  view?: 'application' | 'reviewing' | 'rejected';
  status?: {
    title: string;
    body: string;
    action: string;
    onAction: () => void;
  };
}) {
  const t = useTranslations('artistProgram.apply');
  const isChina = SITE === 'china';
  const channels = isChina ? domesticChannels : globalChannels;
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!open) setForm(emptyForm());
  }, [open]);
  const toggle = (key: 'roles' | 'creationFields' | 'usageScenes', value: string) =>
    setForm((current) => ({
      ...current,
      [key]: current[key].includes(value) ? current[key].filter((item) => item !== value) : [...current[key], value],
    }));
  const update = (key: keyof FormState, value: string | boolean) =>
    setForm((current) => ({ ...current, [key]: value }));
  const updateSocial = (channel: Channel, value: string) =>
    setForm((current) => ({ ...current, socials: { ...current.socials, [channel]: value } }));
  const handleSubmit = async () => {
    const selectedSocials = channels.filter((channel) => form.socials[channel].trim());
    if (
      !form.roles.length ||
      (!isChina && (!form.country || (form.country === 'other' && !form.countryOther.trim()))) ||
      !form.personalIntro.trim() ||
      !form.creationFields.length ||
      (form.creationFields.includes('other') && !form.creationFieldOther.trim()) ||
      !form.usageScenes.length ||
      (form.usageScenes.includes('other') && !form.usageSceneOther.trim()) ||
      (form.usageScenes.includes('workplace') && !form.workplaceDescription.trim()) ||
      !selectedSocials.length ||
      (isChina && !form.followerRange) ||
      !form.featuredWorkUrl.trim() ||
      !form.contact.trim() ||
      !form.agreed
    ) {
      toast({ type: 'error', message: t('required') });
      return;
    }
    setSubmitting(true);
    try {
      const payload: ArtistProgramApplicationRequest & { country?: string } = {
        roles: form.roles as ArtistProgramApplicationRequest['roles'],
        country: isChina ? undefined : form.country === 'other' ? form.countryOther.trim() : form.country,
        personal_intro: form.personalIntro.trim(),
        school: isChina ? form.school.trim() || undefined : undefined,
        major: isChina ? form.major.trim() || undefined : undefined,
        creation_fields: form.creationFields as ArtistProgramApplicationRequest['creation_fields'],
        creation_field_other: form.creationFieldOther.trim() || undefined,
        usage_scenes: form.usageScenes as ArtistProgramApplicationRequest['usage_scenes'],
        usage_scene_other: form.usageSceneOther.trim() || undefined,
        workplace_description: form.workplaceDescription.trim() || undefined,
        social_accounts: selectedSocials.map((channel) => ({ channel, link: form.socials[channel].trim() })),
        follower_range: isChina ? (form.followerRange as ArtistProgramApplicationRequest['follower_range']) : undefined,
        featured_work_url: form.featuredWorkUrl.trim(),
        contact: form.contact.trim(),
        authorization_agreed: true,
      };
      await submitApplication(payload);
      onSubmitted();
    } catch (error) {
      toast({ type: 'error', message: error instanceof Error ? error.message : t('submitFailed') });
    } finally {
      setSubmitting(false);
    }
  };
  const label = (key: string) => t(`options.${key}` as never);
  const inputClass = 'h-11 w-full rounded-lg border border-white/12 bg-white/5 px-3';
  const isApplicationView = view === 'application';
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className={isApplicationView ? 'max-w-[760px]' : 'max-w-[520px]'} onClose={onClose}>
        {isApplicationView ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('title')}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] space-y-5 overflow-y-auto pr-1 text-sm text-white/80">
              <fieldset>
                <legend className="mb-2 font-medium text-white">{t('applicationType')} *</legend>
                <div className="flex flex-wrap gap-2">
                  {(isChina ? ['emerging_creator', 'artist'] : ['artist', 'mentor']).map((key) => (
                    <label key={key} className="rounded-full border border-white/12 px-3 py-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={form.roles.includes(key)}
                        onChange={() => toggle('roles', key)}
                      />
                      {label(key)}
                    </label>
                  ))}
                </div>
              </fieldset>
              {!isChina && (
                <label className="block" htmlFor="artist-program-country">
                  <span className="mb-2 block font-medium text-white">{t('country')} *</span>
                  <select
                    id="artist-program-country"
                    name="country"
                    className={inputClass}
                    style={{ colorScheme: 'dark' }}
                    value={form.country}
                    onChange={(event) => update('country', event.target.value)}
                  >
                    <option value="" disabled className="bg-[#2c2e33] text-white">
                      {t('countryPlaceholder')}
                    </option>
                    {globalCountries.map((country) => (
                      <option key={country} value={country} className="bg-[#2c2e33] text-white">
                        {country}
                      </option>
                    ))}
                    <option value="other" className="bg-[#2c2e33] text-white">
                      {label('other')}
                    </option>
                  </select>
                  {form.country === 'other' && (
                    <input
                      className={`${inputClass} mt-3`}
                      maxLength={MAX.other}
                      value={form.countryOther}
                      onChange={(event) => update('countryOther', event.target.value)}
                      placeholder={t('countryOtherPlaceholder')}
                    />
                  )}
                </label>
              )}
              <label className="block" htmlFor="artist-program-personal-intro">
                <span className="mb-2 block font-medium text-white">{t('personalIntro')} *</span>
                <Textarea
                  id="artist-program-personal-intro"
                  value={form.personalIntro}
                  maxLength={MAX.personalIntro}
                  onChange={(e) => update('personalIntro', e.target.value)}
                  placeholder={t('personalIntroPlaceholder')}
                />
                <span className="mt-1 block text-right text-white/45 text-xs">
                  {form.personalIntro.length}/{MAX.personalIntro}
                </span>
              </label>
              {isChina && (
                <fieldset>
                  <legend className="mb-2 font-medium text-white">{t('school')} </legend>
                  <div className="grid grid-cols-2 gap-4">
                    <label>
                      <input
                        className={inputClass}
                        maxLength={MAX.school}
                        value={form.school}
                        onChange={(e) => update('school', e.target.value)}
                        placeholder={t('schoolPlaceholder')}
                      />
                    </label>
                    <label>
                      <input
                        className={inputClass}
                        maxLength={MAX.major}
                        value={form.major}
                        onChange={(e) => update('major', e.target.value)}
                        placeholder={t('majorPlaceholder')}
                      />
                    </label>
                  </div>
                </fieldset>
              )}
              <fieldset>
                <legend className="mb-2 font-medium text-white">{t('creationFields')} *</legend>
                <div className="flex flex-wrap gap-2">
                  {fields.map((key) => (
                    <label key={key} className="rounded-full border border-white/12 px-3 py-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={form.creationFields.includes(key)}
                        onChange={() => toggle('creationFields', key)}
                      />
                      {label(key)}
                    </label>
                  ))}
                </div>
                {form.creationFields.includes('other') && (
                  <input
                    className={`${inputClass} mt-3`}
                    maxLength={MAX.other}
                    value={form.creationFieldOther}
                    onChange={(e) => update('creationFieldOther', e.target.value)}
                    placeholder={t('otherPlaceholder')}
                  />
                )}
              </fieldset>
              <fieldset>
                <legend className="mb-1 font-medium text-white">{t('usageScenes')} *</legend>
                <p className="mb-2 text-white/55">{t('usageScenesHint')}</p>
                <div className="flex flex-wrap gap-2">
                  {scenes.map((key) => (
                    <label key={key} className="rounded-full border border-white/12 px-3 py-2">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={form.usageScenes.includes(key)}
                        onChange={() => toggle('usageScenes', key)}
                      />
                      {label(key)}
                    </label>
                  ))}
                </div>
                {form.usageScenes.includes('other') && (
                  <input
                    className={`${inputClass} mt-3`}
                    maxLength={MAX.other}
                    value={form.usageSceneOther}
                    onChange={(e) => update('usageSceneOther', e.target.value)}
                    placeholder={t('otherPlaceholder')}
                  />
                )}
              </fieldset>
              {form.usageScenes.includes('workplace') && (
                <label className="block">
                  <span className="mb-2 block">{t('workplaceDescription')} *</span>
                  <input
                    className={inputClass}
                    value={form.workplaceDescription}
                    onChange={(e) => update('workplaceDescription', e.target.value)}
                  />
                </label>
              )}
              <fieldset>
                <legend className="mb-2 font-medium text-white">{t('socialAccounts')} *</legend>
                <p className="mb-3 text-white/55">{t('socialAccountsHint')}</p>
                <div className="grid grid-cols-2 gap-3">
                  {channels.map((channel) => (
                    <label key={channel}>
                      <span className="mb-1 block">{label(channel)}</span>
                      <input
                        className={inputClass}
                        type={isChina && channel !== 'video_account' ? 'url' : 'text'}
                        value={form.socials[channel]}
                        onChange={(e) => updateSocial(channel, e.target.value)}
                        placeholder={t(`placeholders.${channel}` as never)}
                      />
                    </label>
                  ))}
                </div>
              </fieldset>
              {isChina && (
                <fieldset>
                  <legend className="mb-2 font-medium text-white">{t('followerRange')} *</legend>
                  <div className="flex flex-wrap gap-4">
                    {['up_to_1000', 'from_1000_to_5000', 'from_5000_to_10000', 'at_least_10000'].map((key) => (
                      <label key={key}>
                        <input
                          type="radio"
                          name="artist-follower-range"
                          className="mr-2"
                          checked={form.followerRange === key}
                          onChange={() => update('followerRange', key)}
                        />
                        {label(key)}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
              <div className="grid gap-4">
                <label>
                  <span className="mb-2 block">{t('featuredWorkUrl')} *</span>
                  <input
                    type="url"
                    className={inputClass}
                    value={form.featuredWorkUrl}
                    onChange={(e) => update('featuredWorkUrl', e.target.value)}
                  />
                </label>
                <label>
                  <span className="mb-2 block">{isChina ? t('contact') : t('email')} *</span>
                  <input
                    type="text"
                    className={inputClass}
                    value={form.contact}
                    onChange={(e) => update('contact', e.target.value)}
                  />
                </label>
              </div>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.agreed}
                  onChange={(e) => update('agreed', e.target.checked)}
                />
                <span>
                  {t('authorization')}{' '}
                  <Link
                    href="/artist-program/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t('authorizationLink')}
                  </Link>
                </span>
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button type="button" variant="primary" loading={submitting} disabled={submitting} onClick={handleSubmit}>
                {t('submit')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{status?.title}</DialogTitle>
            </DialogHeader>
            {status?.body ? <p className="whitespace-pre-line text-white/62">{status.body}</p> : null}
            {status?.action ? (
              <DialogFooter>
                <Button type="button" variant="primary" onClick={status.onAction}>
                  {status.action}
                </Button>
              </DialogFooter>
            ) : null}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
